import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IntegrationType, Recipe } from '@pdqdev/pdq-shared/dist/entities';
import { DynamicRecipeModels } from '@pdqdev/pdq-shared/dist/interfaces';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, takeUntil } from 'rxjs/operators';
import { TimeUnits } from '@utils/times-utils';
import { IntegrationsService } from '@services/integrations/integrations.service';
import { PopoverService } from '@storybook/services/popover/popover.service';
import { ToastService } from '@storybook/services/toast/toast.service';
import { RecipeEditorComponent } from '../../recipe-editor/recipe-editor.component';
import { DeleteRecipeComponent } from './delete-recipe/delete-recipe.component';

enum RecipeAction {
  DELETE = 'delete',
  EDIT = 'edit',
}

const ACTION_TO_CONFIG = new Map<RecipeAction, object>([
  [RecipeAction.EDIT, { label: 'Edit', destructive: false }],
  [RecipeAction.DELETE, { label: 'Delete', destructive: true }],
]);
@Component({
  selector: 'recipe',
  templateUrl: './recipe.component.html',
  styleUrls: ['./recipe.component.scss'],
  standalone: false,
})
export class RecipeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() recipe: Recipe;
  @Input() integrationType: IntegrationType;

  @ViewChild('popoverContent') popoverContent;

  dialogRef: MatDialogRef<RecipeEditorComponent | DeleteRecipeComponent>;
  recipeActions = [
    {
      id: RecipeAction.EDIT,
      ...ACTION_TO_CONFIG.get(RecipeAction.EDIT),
    },
    {
      id: RecipeAction.DELETE,
      ...ACTION_TO_CONFIG.get(RecipeAction.DELETE),
    },
  ];
  variables: DynamicRecipeModels.Variable[] = [];
  destroy$ = new Subject();
  toggleChange$ = new Subject();

  constructor(
    private popoverService: PopoverService,
    private dialog: MatDialog,
    private integrationsService: IntegrationsService,
    private toastService: ToastService,
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.recipe) {
      this.initProps();
    }
  }

  initSubscriptions() {
    this.toggleChange$
      .pipe(takeUntil(this.destroy$), debounceTime(TimeUnits._SECOND_IN_MS * 1), distinctUntilChanged())
      .subscribe((newIsActive: boolean) => this.toggleRecipe(newIsActive));
  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
  }
  toggleRecipe(newIsActive: boolean) {
    this.integrationsService.toggleRecipe(this.recipe, newIsActive, this.integrationType).subscribe(
      (res) => {},
      (err) => {
        this.toastService.showToast({
          mode: 'danger',
          message: `Failed to toggle ${newIsActive ? 'on' : 'off'} recipe`,
        });
      },
    );
  }
  handleToggleClick(newIsActive: boolean) {
    this.toggleChange$.next(newIsActive);
  }
  ngOnInit(): void {
    this.initProps();
    this.initSubscriptions();
  }

  handleAction(action: RecipeAction) {
    this.popoverService.setState(true);
    switch (action) {
      case RecipeAction.DELETE:
        this.deleteRecipe();
        break;
      case RecipeAction.EDIT:
        this.dialogRef = this.dialog.open(RecipeEditorComponent, {
          backdropClass: 'global_modal_overlay_subdued',
          panelClass: 'overflow-visible-class',
          data: { recipeTemplate: this.recipe.template, integrationType: this.integrationType, recipe: this.recipe },
        });
        break;
    }
  }

  private deleteRecipe() {
    this.dialogRef = this.dialog.open(DeleteRecipeComponent, {
      backdropClass: 'global_modal_overlay_subdued',
    });

    this.dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.integrationsService
          .deleteRecipe(this.recipe, this.integrationType)
          .pipe(first())
          .subscribe(
            () => {
              this.toastService.showToast({ message: 'Recipe deleted', mode: 'success' });
            },
            (err) => {
              console.log(err);
              this.toastService.showToast({ message: 'Failed to delete recipe', mode: 'danger' });
            },
          );
      }
    });
  }
  private initProps() {
    this.variables = this.recipe.metadata?.variables ? Object.values(this.recipe.metadata.variables) : [];
    this.recipeActions = [
      {
        id: RecipeAction.DELETE,
        ...ACTION_TO_CONFIG.get(RecipeAction.DELETE),
      },
    ];

    if (this.recipe.template?.dynamicPartsConfig) {
      this.recipeActions.unshift({
        id: RecipeAction.EDIT,
        ...ACTION_TO_CONFIG.get(RecipeAction.EDIT),
      });
    }
  }
}
